import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
 AlertCircle,
 AlertTriangle,
 CheckCircle2,
 Info,
 Loader2,
 X,
 type LucideIcon,
} from 'lucide-react'
import { semanticSolidSurfaceClass, type SemanticBadgeTone } from 'helpers/kpiIconClass'
import { cn } from '../lib/utils'

export type ErpToastVariant = 'success' | 'error' | 'warning' | 'info' | 'loading'

export interface ErpToastCardProps {
 variant: ErpToastVariant
 message: React.ReactNode
 description?: React.ReactNode
 onDismiss: () => void
 className?: string
}

const TOAST_TONE: Record<ErpToastVariant, SemanticBadgeTone> = {
 success: 'success',
 error: 'danger',
 warning: 'warning',
 info: 'info',
 loading: 'info',
}

function solidCloseClass(variant: ErpToastVariant): string {
 if (variant === 'warning') {
  return 'border-black/15 bg-black/10 hover:bg-black/15 hover:border-black/20'
 }
 return 'border-white/25 bg-white/15 hover:bg-white/25 hover:border-white/35'
}

export function ErpToastCard({
 variant,
 message,
 description,
 onDismiss,
 className,
}: ErpToastCardProps) {
 const { t } = useTranslation()
 const isLoading = variant === 'loading'
 const variantIcons: Record<Exclude<ErpToastVariant, 'loading'>, LucideIcon> = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
 }
 const Icon = isLoading ? Loader2 : variantIcons[variant]
 const tone = TOAST_TONE[variant]

 return (
 <div
 role="status"
 aria-live="polite"
 className={cn(
 'erp-toast-card',
 semanticSolidSurfaceClass(tone),
 className,
 )}
 >
 <div className="erp-toast-card__icon border-transparent bg-white/18" aria-hidden>
 <Icon className={cn('h-5 w-5', isLoading && 'animate-spin')} strokeWidth={2.35} />
 </div>
 <div className="erp-toast-card__body">
 <p className="erp-toast-card__title">{message}</p>
 {description ? <p className="erp-toast-card__desc">{description}</p> : null}
 </div>
 <button
 type="button"
 className={cn('erp-toast-card__close', solidCloseClass(variant))}
 onClick={onDismiss}
 aria-label={t('ui.closeNotification')}
 >
 <X className="h-4 w-4" strokeWidth={2.25} />
 </button>
 </div>
 )
}

type ErpToastOptions = {
 duration?: number
 description?: React.ReactNode
}

function push(variant: ErpToastVariant, message: React.ReactNode, options?: ErpToastOptions) {
 const duration =
 options?.duration ??
 (variant === 'error' ? 5200 : variant === 'warning' ? 6500 : 4000)

 return toast.custom(
 (id) => (
 <ErpToastCard
 variant={variant}
 message={message}
 description={options?.description}
 onDismiss={() => toast.dismiss(id)}
 />
 ),
 { duration: variant === 'loading' ? Infinity : duration },
 )
}

/** App-wide Sonner toasts — custom card UI (sync, helpers, dialogs). */
export const erpToast = {
 success: (message: React.ReactNode, options?: ErpToastOptions) =>
 push('success', message, options),
 error: (message: React.ReactNode, options?: ErpToastOptions) =>
 push('error', message, options),
 warning: (message: React.ReactNode, options?: ErpToastOptions) =>
 push('warning', message, options),
 info: (message: React.ReactNode, options?: ErpToastOptions) =>
 push('info', message, options),
 message: (message: React.ReactNode, options?: ErpToastOptions) =>
 push('info', message, options),
 loading: (message: React.ReactNode, options?: Omit<ErpToastOptions, 'duration'>) =>
 push('loading', message, options),
 dismiss: toast.dismiss,
}
